import { Controller } from "@hotwired/stimulus"
import Konva from "konva"
import { editorStore, demoPlot } from "../editor/store"
import { computeScale, metresToPx, pxToMetres, polygonBoundingBox } from "../editor/projection"
import { typeById } from "../editor/object_types"
import { getSunPosition, sunArcSamples, shadowDirection, toUtcDate, estimateUtcOffsetHours } from "../editor/sun"
import { computeShadowPolygon } from "../editor/shadows"
import { computeHeatmap, heatmapToImageData } from "../editor/heatmap"
import { validate as validateNorms, violationsByObjectId } from "../editor/norms"
import { loadRuleSet } from "../editor/rule_set"
import { ViolationRenderer } from "../editor/violation_renderer"
import { polygonEdges, edgeLengthM, edgeMidpointM, edgeAngleRad } from "../editor/edges"
import { boundingBox as graphBoundingBox } from "../floorplan/graph"
import { roomColor } from "../floorplan/room_types"
import { SatelliteLayer, createAttributionEl } from "../editor/satellite_layer"
import { createCompassEl } from "../editor/compass"

const SNAP_METRES = 1

export default class extends Controller {
  static targets = ["stage"]

  connect() {
    // `data-read-only="true"` strips all editing UI (drag, select, transform,
    // tap-to-place, edge-edit) while keeping pan/zoom — pure view-only mode.
    // No view currently emits this attribute (public share is now an in-memory
    // sandbox, owner edit needs full editing), so this branch is effectively
    // off everywhere; kept as a hook for future strict-view contexts (e.g. a
    // printable snapshot).
    this._readOnly = this.element.dataset.readOnly === "true"

    // Server-hydrated project state (Stage 5c). Must run before the demo
    // plot fallback — otherwise /projects/:id/edit would briefly render
    // demo geometry then snap to the user's saved state on the next tick.
    this.#hydrateFromServer()

    if (!editorStore.plot) editorStore.setPlot(demoPlot())

    this.stage = new Konva.Stage({
      container: this.stageTarget,
      width: this.stageTarget.clientWidth,
      height: this.stageTarget.clientHeight,
    })

    // Satellite tile backdrop — sits below everything so the plot outline,
    // grid, objects, shadows, and heatmap all draw on top. Disabled by
    // default; opt-in from the "Вид" panel (Stage 8a).
    this.mapTileLayer = new Konva.Layer({ listening: false })
    this.plotLayer = new Konva.Layer({ listening: false })
    // heatmap sits on top of the plot but below shadows/objects so the
    // gradient never obscures the user's buildings.
    this.heatmapLayer = new Konva.Layer({ listening: false })
    this.shadowLayer = new Konva.Layer({ listening: false })
    this.sunArcLayer = new Konva.Layer({ listening: false })
    this.objectLayer = new Konva.Layer({ listening: !this._readOnly })
    // Violation outlines sit above objects so red dashes are always visible,
    // but below the transformer so resize anchors stay on top. Non-listening:
    // pointer events still go to the real object rects.
    this.violationLayer = new Konva.Layer({ listening: false })
    // Edge-length labels (Stage 6d) sit above objects so the label stays
    // legible when an object overlaps the plot boundary. Listening so taps
    // on a label open the edge-edit popover (Stage 6d.2). Labels
    // `cancelBubble` on click to stop the stage's tap-to-place handler
    // from firing; the hit area matches the visible pill, so taps on empty
    // plot next to a label still reach the stage.
    // Read-only: no click-to-edit, so keep labels but drop listening.
    this.edgeLayer = new Konva.Layer({ listening: !this._readOnly })
    this.uiLayer = new Konva.Layer()
    this.stage.add(this.mapTileLayer, this.plotLayer, this.heatmapLayer, this.shadowLayer, this.sunArcLayer, this.objectLayer, this.violationLayer, this.edgeLayer, this.uiLayer)

    // MapTiler/OSM attribution — HTML overlay instead of Konva.Text so the
    // links are clickable (ToS requires attribution to link to
    // maptiler.com/copyright and openstreetmap.org/copyright). Sits on top
    // of the Konva stage container via absolute positioning.
    this.attributionEl = createAttributionEl()
    this.stageTarget.appendChild(this.attributionEl)

    // Static compass — stage axes are never rotated (north is up), so a
    // fixed indicator is enough. Added after Konva.Stage init because Konva
    // wipes the container's innerHTML on construction.
    this.compassEl = createCompassEl()
    this.stageTarget.appendChild(this.compassEl)

    this.satelliteLayer = new SatelliteLayer({
      tileLayer: this.mapTileLayer,
      attributionEl: this.attributionEl,
    })

    this.violationRenderer = new ViolationRenderer({
      stage: this.stage,
      layer: this.violationLayer,
      container: this.stageTarget,
      metresToPx,
    })

    if (!this._readOnly) {
      this.transformer = new Konva.Transformer({
        rotateEnabled: true,
        rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315],
        keepRatio: false,
        borderStroke: "#4338ca",
        anchorStroke: "#4338ca",
        anchorFill: "#ffffff",
      })
      this.uiLayer.add(this.transformer)
    }

    this.resizeObserver = new ResizeObserver(() => this.fitAndRedraw())
    this.resizeObserver.observe(this.stageTarget)

    if (!this._readOnly) {
      this.#bindDrop()
      this.#bindStageSelection()
      this.#bindHouseOpen()
      this.#bindPendingEscape()
      this.#bindLongPress()
      this.#bindEdgePreview()
      this.#syncPendingCursor()
    }
    this.#subscribeStore()
    this.#bindVisibility()
    this.#bindCanvasGestures()
    this.fitAndRedraw()

    if (import.meta.env.DEV) window.__editor = this
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this._unsubscribe && this._unsubscribe()
    this._unbindDrop && this._unbindDrop()
    this._unbindVisibility && this._unbindVisibility()
    this._unbindPendingEscape && this._unbindPendingEscape()
    this._unbindLongPress && this._unbindLongPress()
    this._unbindGestures && this._unbindGestures()
    this._unsubscribePending && this._unsubscribePending()
    this._unbindEdgePreview && this._unbindEdgePreview()
    this.violationRenderer?.destroy()
    this.violationRenderer = null
    this.attributionEl?.remove()
    this.attributionEl = null
    this.compassEl?.remove()
    this.compassEl = null
    this.stage?.destroy()
    this.stage = null
  }

  fitAndRedraw() {
    if (!this.stage) return
    const w = this.stageTarget.clientWidth
    const h = this.stageTarget.clientHeight
    this.stage.size({ width: w, height: h })

    if (!editorStore.plot) {
      this.plotLayer.destroyChildren()
      this.objectLayer.destroyChildren()
      this.plotLayer.batchDraw()
      this.objectLayer.batchDraw()
      return
    }

    const bb = polygonBoundingBox(editorStore.plot.polygonM)
    if (!isFinite(bb.minX) || bb.widthM <= 0 || bb.heightM <= 0) return

    this.scale = computeScale(w, h, bb.widthM, bb.heightM, { x: bb.minX, y: bb.minY })
    this.#renderSatellite()
    this.#renderPlot()
    this.#renderObjects()
    this.#renderInsolation()
    this.#renderSunArc()
    this.#renderViolations()
    this.#renderEdgeLabels()
    this.#reattachTransformer()
  }

  #hydrateFromServer() {
    const el = document.getElementById("initial-project-state")
    if (!el) return
    let state
    try { state = JSON.parse(el.textContent) } catch (e) { return }
    if (!state || typeof state !== "object") return
    // Empty {} from a freshly-created project — fall through to demo plot.
    if (!state.plot && (!state.objects || state.objects.length === 0)) return
    editorStore.restoreDraft({
      plot: state.plot || null,
      objects: Array.isArray(state.objects) ? state.objects : [],
      norms: state.norms,
      ui: state.ui,
    })
  }

  #bindDrop() {
    const container = this.stageTarget
    const onDragOver = (e) => {
      if (e.dataTransfer?.types?.includes("application/x-plot-object-type")) {
        e.preventDefault()
        e.dataTransfer.dropEffect = "copy"
      }
    }
    const onDrop = (e) => {
      const typeId = e.dataTransfer?.getData("application/x-plot-object-type")
      if (!typeId) return
      e.preventDefault()
      const type = typeById(typeId)
      if (!type || !this.scale) return
      const rect = container.getBoundingClientRect()
      const pxPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      const metres = pxToMetres(pxPoint, this.scale)
      const added = editorStore.addObject({
        type: type.id,
        name: type.name,
        xM: snap(metres.xM - type.defaultSizeM.width / 2),
        yM: snap(metres.yM - type.defaultSizeM.height / 2),
        widthM: type.defaultSizeM.width,
        heightM: type.defaultSizeM.height,
        heightMetresAboveGround: type.defaultHeightM ?? 0,
        fillColor: type.fillColor,
        strokeColor: type.strokeColor,
      })
      // addObject already emitted objects:change; setting selectedId now and
      // dispatching selection:change a microtask later lets renderObjects
      // finish first so the Transformer attaches to a freshly-drawn node.
      queueMicrotask(() => editorStore.selectObject(added.id))
    }
    container.addEventListener("dragover", onDragOver)
    container.addEventListener("drop", onDrop)
    this._unbindDrop = () => {
      container.removeEventListener("dragover", onDragOver)
      container.removeEventListener("drop", onDrop)
    }
  }

  #bindStageSelection() {
    this.stage.on("click tap", (e) => {
      // A 2-finger pinch fires touchstart+touchend on the stage; Konva can
      // synthesize `tap` on lift. Without this guard, a pinch that ends with
      // `pendingAddType` armed would drop an object at the pinch midpoint.
      if (this._gestureInProgress) return

      // iOS still fires `tap` then synthetic `click` within a few ms; 100ms
      // dedup swallows the dup without blocking rapid taps on different objects.
      const now = Date.now()
      if (this._lastStageTapAt && now - this._lastStageTapAt < 100) return
      this._lastStageTapAt = now

      const pendingId = editorStore.pendingAddType

      // Tap-to-place happens ONLY when the tap lands on empty plot (stage
      // background). Tapping an existing object while a type is armed is
      // treated as "oops, I want that object instead" — cancel pending and
      // fall through to normal selection.
      if (pendingId && e.target === this.stage && this.scale) {
        const type = typeById(pendingId)
        if (type) {
          const pos = this.stage.getPointerPosition()
          if (pos) {
            const metres = pxToMetres(pos, this.scale)
            const added = editorStore.addObject({
              type: type.id,
              name: type.name,
              xM: snap(metres.xM - type.defaultSizeM.width / 2),
              yM: snap(metres.yM - type.defaultSizeM.height / 2),
              widthM: type.defaultSizeM.width,
              heightM: type.defaultSizeM.height,
              heightMetresAboveGround: type.defaultHeightM ?? 0,
              fillColor: type.fillColor,
              strokeColor: type.strokeColor,
            })
            editorStore.selectObject(added.id)
          }
        }
        editorStore.setPendingAddType(null)
        return
      }

      // Cancel pending before selecting: a tap on an object is interpreted
      // as "select that object" regardless of pending-add state.
      if (pendingId) editorStore.setPendingAddType(null)

      if (e.target === this.stage) {
        editorStore.selectObject(null)
        return
      }
      const id = e.target.id()
      if (id) editorStore.selectObject(id)
    })
  }

  #bindHouseOpen() {
    // Double-click / double-tap a house → open the floor-plan editor overlay
    // (house_mode_controller listens for `house:open`). Bound on the stage; the
    // stage is destroyed in disconnect(), which removes this listener too.
    this.stage.on("dblclick dbltap", (e) => {
      const id = e.target?.id?.()
      if (!id) return
      const obj = editorStore.objects.find((o) => o.id === id)
      if (obj?.type === "house") {
        window.dispatchEvent(new CustomEvent("house:open", { detail: { id } }))
      }
    })
  }

  #bindVisibility() {
    // iOS Safari suspends canvas contexts when the tab is hidden; coming
    // back, the stage sometimes loses its framebuffer until a redraw is
    // forced. Cheap insurance: batchDraw every layer on visibility return.
    this._onVisibility = () => {
      if (document.visibilityState === "visible" && this.stage) {
        this.fitAndRedraw()
      }
    }
    document.addEventListener("visibilitychange", this._onVisibility)
    this._unbindVisibility = () => document.removeEventListener("visibilitychange", this._onVisibility)
  }

  #emitSelectionRect() {
    const id = editorStore.selectedId
    if (!id || !this.objectLayer || !this.stageTarget) {
      window.dispatchEvent(new CustomEvent("editor:selection-clear"))
      return
    }
    const node = this.objectLayer.findOne(`#${cssId(id)}`)
    if (!node) {
      window.dispatchEvent(new CustomEvent("editor:selection-clear"))
      return
    }
    const stageBounds = this.stageTarget.getBoundingClientRect()
    const bb = node.getClientRect({ skipStroke: true })
    const rect = {
      top: stageBounds.top + bb.y,
      left: stageBounds.left + bb.x,
      width: bb.width,
      height: bb.height,
      bottom: stageBounds.top + bb.y + bb.height,
      right: stageBounds.left + bb.x + bb.width,
    }
    // The Transformer draws a rotation anchor above the top edge at
    // `rotateAnchorOffset` stage-units. Floating UI (contextual toolbar)
    // needs to clear it, so we pass the scaled offset alongside the rect.
    const anchorOffsetTop = this.transformer?.nodes?.().length
      ? (this.transformer.rotateAnchorOffset?.() ?? 50) * (this.stage?.scaleY() ?? 1)
      : 0
    window.dispatchEvent(new CustomEvent("editor:selection-rect", {
      detail: { rect, anchorOffsetTop },
    }))
  }

  #bindLongPress() {
    // Touch-only long-press on an object → dispatch object:long-press so the
    // bottom sheet can open the properties form. 500ms with a small drift
    // threshold mirrors platform conventions (iOS/Android).
    const LONG_PRESS_MS = 500
    const DRIFT_PX = 10
    let timer = null
    let startId = null
    let startX = 0, startY = 0

    const clear = () => {
      if (timer) { clearTimeout(timer); timer = null }
      startId = null
    }
    this._onTouchStart = (e) => {
      if (e.evt.touches && e.evt.touches.length > 1) { clear(); return }
      const target = e.target
      if (!target || target === this.stage) { clear(); return }
      // Transformer anchors live in uiLayer; only objectLayer hits count.
      if (target.getLayer && target.getLayer() !== this.objectLayer) { clear(); return }
      const id = target.id && target.id()
      if (!id) { clear(); return }
      startId = id
      const touch = e.evt.touches ? e.evt.touches[0] : e.evt
      startX = touch.clientX
      startY = touch.clientY
      timer = setTimeout(() => {
        if (!startId) return
        editorStore.selectObject(startId)
        window.dispatchEvent(new CustomEvent("object:long-press", { detail: { id: startId } }))
        clear()
      }, LONG_PRESS_MS)
    }
    this._onTouchMove = (e) => {
      if (!timer) return
      const touch = e.evt.touches ? e.evt.touches[0] : e.evt
      if (!touch) return
      const dx = touch.clientX - startX
      const dy = touch.clientY - startY
      if (dx * dx + dy * dy > DRIFT_PX * DRIFT_PX) clear()
    }
    this.stage.on("touchstart", this._onTouchStart)
    this.stage.on("touchmove", this._onTouchMove)
    this.stage.on("touchend touchcancel dragstart", clear)
    this._unbindLongPress = () => {
      this.stage.off("touchstart", this._onTouchStart)
      this.stage.off("touchmove", this._onTouchMove)
      this.stage.off("touchend touchcancel dragstart", clear)
      clear()
    }
  }

  #bindCanvasGestures() {
    // Two-finger pinch-to-zoom + pan on the Konva stage. Single-finger touch
    // falls through to Konva's own event path (selection, long-press, object
    // drag). Mouse wheel is intentionally NOT bound here — desktop users
    // already have drag-n-drop and sidebar; wheel-zoom can come later.
    const container = this.stageTarget
    const MIN_SCALE = 0.2
    const MAX_SCALE = 5
    let initial = null

    const pair = (touches) => touches.length >= 2 ? [touches[0], touches[1]] : null
    const distance = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
    const center = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 })

    this._onGestureStart = (e) => {
      const fingers = pair(e.touches)
      if (!fingers || !this.stage) { initial = null; return }
      const box = container.getBoundingClientRect()
      initial = {
        distance: distance(fingers[0], fingers[1]),
        center: center(fingers[0], fingers[1]),
        scale: this.stage.scaleX(),
        position: { x: this.stage.x(), y: this.stage.y() },
        boxLeft: box.left,
        boxTop: box.top,
      }
      this._gestureInProgress = true
      e.preventDefault()
    }

    this._onGestureMove = (e) => {
      if (!initial || !this.stage) return
      const fingers = pair(e.touches)
      if (!fingers) return
      e.preventDefault()

      const d = distance(fingers[0], fingers[1])
      const c = center(fingers[0], fingers[1])
      const ratio = d / initial.distance
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, initial.scale * ratio))

      // Keep the local point under the initial finger-midpoint anchored to
      // the current finger-midpoint: screen = position + local * scale, so
      // local = (screen - position) / scale; new position = c - local * scale.
      const focalLocalX = (initial.center.x - initial.boxLeft - initial.position.x) / initial.scale
      const focalLocalY = (initial.center.y - initial.boxTop - initial.position.y) / initial.scale
      const newPosition = {
        x: c.x - initial.boxLeft - focalLocalX * newScale,
        y: c.y - initial.boxTop - focalLocalY * newScale,
      }

      this.stage.scale({ x: newScale, y: newScale })
      this.stage.position(newPosition)
      this.stage.batchDraw()
      this.#emitSelectionRect()
    }

    this._onGestureEnd = (e) => {
      // Reset on any lift. Any finger count change invalidates the snapshot
      // (3→2 fingers leaves a stale initial.center otherwise); touchstart
      // will re-snapshot if 2 fingers remain.
      initial = null
      // Defer clearing the guard by one frame so the Konva-synthesized `tap`
      // that may follow touchend still sees the flag and is swallowed.
      if (this._gestureInProgress) {
        requestAnimationFrame(() => { this._gestureInProgress = false })
      }
      // After a pinch or pan, the Konva stage transform has changed but the
      // currently-rendered satellite tiles were selected for the PREVIOUS
      // viewport. Refresh so the user sees imagery for the area they just
      // panned to / zoomed into. Konva auto-transforms existing tiles during
      // the gesture so they stay registered with the plot; we only need to
      // re-select which tiles to load now.
      if (editorStore.ui?.satelliteBg?.enabled) this.#renderSatellite()
    }

    // Desktop: trackpad/mouse wheel. macOS trackpad two-finger pinch fires
    // `wheel` with `ctrlKey=true` and a small deltaY; two-finger pan fires
    // `wheel` without ctrlKey and with deltaX/Y. Regular mouse wheel is
    // treated as zoom when Ctrl/Cmd held, else pan (familiar pattern from
    // Figma / Miro / Google Maps).
    this._onWheel = (e) => {
      if (!this.stage) return
      e.preventDefault()
      const box = container.getBoundingClientRect()
      const curScale = this.stage.scaleX()
      const curPos = this.stage.position()

      if (e.ctrlKey || e.metaKey) {
        // Pinch-zoom: anchor the point under the cursor so it stays put as
        // we scale. Exponential factor from deltaY keeps trackpad and mouse
        // wheel feeling similar (small deltas = small zoom).
        const focal = { x: e.clientX - box.left, y: e.clientY - box.top }
        const zoomFactor = Math.exp(-e.deltaY * 0.01)
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, curScale * zoomFactor))
        if (newScale === curScale) return
        const focalLocalX = (focal.x - curPos.x) / curScale
        const focalLocalY = (focal.y - curPos.y) / curScale
        this.stage.scale({ x: newScale, y: newScale })
        this.stage.position({
          x: focal.x - focalLocalX * newScale,
          y: focal.y - focalLocalY * newScale,
        })
      } else {
        // Two-finger pan (trackpad) or shift+wheel (mouse). Just shift the
        // stage by the delta — Konva re-renders children at new positions.
        this.stage.position({ x: curPos.x - e.deltaX, y: curPos.y - e.deltaY })
      }
      this.stage.batchDraw()
      this.#emitSelectionRect()
      this.#scheduleSatelliteRefresh()
    }

    // passive:false so preventDefault actually blocks the browser's own
    // pan/zoom on the stage area.
    container.addEventListener("touchstart", this._onGestureStart, { passive: false })
    container.addEventListener("touchmove", this._onGestureMove, { passive: false })
    container.addEventListener("touchend", this._onGestureEnd)
    container.addEventListener("touchcancel", this._onGestureEnd)
    container.addEventListener("wheel", this._onWheel, { passive: false })

    this._unbindGestures = () => {
      container.removeEventListener("touchstart", this._onGestureStart)
      container.removeEventListener("touchmove", this._onGestureMove)
      container.removeEventListener("touchend", this._onGestureEnd)
      container.removeEventListener("touchcancel", this._onGestureEnd)
      container.removeEventListener("wheel", this._onWheel)
      if (this._satRefreshTimer) { clearTimeout(this._satRefreshTimer); this._satRefreshTimer = null }
    }
  }

  // Debounced satellite re-render for continuous gesture streams (wheel has
  // no "end" event — user's fingers may keep moving on the trackpad). ~180ms
  // feels responsive without re-fetching dozens of tile batches mid-gesture.
  #scheduleSatelliteRefresh() {
    if (!editorStore.ui?.satelliteBg?.enabled) return
    if (this._satRefreshTimer) clearTimeout(this._satRefreshTimer)
    this._satRefreshTimer = setTimeout(() => {
      this._satRefreshTimer = null
      this.#renderSatellite()
    }, 180)
  }

  #bindPendingEscape() {
    // Escape cancels a pending add; hotkeys_controller's Escape already
    // clears selection, but pending-add is orthogonal so we handle it here.
    this._onEscape = (e) => {
      if (e.key === "Escape" && editorStore.pendingAddType) {
        editorStore.setPendingAddType(null)
      }
    }
    window.addEventListener("keydown", this._onEscape)
    this._unbindPendingEscape = () => window.removeEventListener("keydown", this._onEscape)
  }

  // Ghost preview (Stage 6d.3): while the edge-edit popover is open the
  // edge_edit_controller debounces onto `edge-edit:preview` with the
  // hypothetical polygon; we draw a dashed indigo segment showing where
  // the edge would land. Clears on close/apply/invalid input.
  #bindEdgePreview() {
    this._onEdgePreview = (e) => this.#renderEdgePreview(e.detail || {})
    window.addEventListener("edge-edit:preview", this._onEdgePreview)
    this._unbindEdgePreview = () => window.removeEventListener("edge-edit:preview", this._onEdgePreview)
  }

  #renderEdgePreview({ edgeIdx, nextPolygon }) {
    if (this._previewLine) { this._previewLine.destroy(); this._previewLine = null }
    if (!this.edgeLayer || !this.scale || !nextPolygon || typeof edgeIdx !== "number") {
      this.edgeLayer?.batchDraw()
      return
    }
    const edges = polygonEdges(nextPolygon)
    const edge = edges[edgeIdx]
    if (!edge) { this.edgeLayer.batchDraw(); return }
    const [a, b] = edge
    const pa = metresToPx({ xM: a[0], yM: a[1] }, this.scale)
    const pb = metresToPx({ xM: b[0], yM: b[1] }, this.scale)
    this._previewLine = new Konva.Line({
      points: [pa.x, pa.y, pb.x, pb.y],
      stroke: "#4f46e5",
      strokeWidth: 3,
      dash: [8, 5],
      listening: false,
      name: "edge-preview",
    })
    this.edgeLayer.add(this._previewLine)
    this.edgeLayer.batchDraw()
  }

  #syncPendingCursor() {
    const apply = () => {
      if (editorStore.pendingAddType) {
        this.stageTarget.dataset.pendingAdd = "1"
      } else {
        delete this.stageTarget.dataset.pendingAdd
      }
      // Re-render edge labels so they vanish while placement is armed and
      // re-appear after the tap lands (or the user cancels with Escape).
      this.#renderEdgeLabels()
    }
    editorStore.addEventListener("pending-add:change", apply)
    this._unsubscribePending = () => editorStore.removeEventListener("pending-add:change", apply)
    apply()
  }

  #subscribeStore() {
    const onPlot = () => {
      // Reset user zoom/pan when the plot itself changes (e.g. loading a
      // different cadastre result) so the new plot isn't stranded offscreen.
      this.stage?.scale({ x: 1, y: 1 })
      this.stage?.position({ x: 0, y: 0 })
      this.#invalidateHeatmap()
      this.fitAndRedraw()
      this.#emitSelectionRect()
    }
    const onObjects = () => {
      this.#invalidateHeatmap()
      this.#renderObjects()
      this.#renderInsolation()
      this.#renderViolations()
      this.#reattachTransformer()
      this.#emitSelectionRect()
    }
    const onSelection = () => { this.#reattachTransformer(); this.#emitSelectionRect() }
    const onSun = () => {
      // Heatmap depends on the DAY (+ UTC offset), not the hour within it.
      // Invalidate only when the date or offset changes — otherwise the
      // hour slider / "play" animation would trigger 96 recomputes in
      // heatmap mode and freeze the browser.
      if (this.#heatmapKeyChanged()) this.#invalidateHeatmap()
      this.#renderInsolation()
      this.#renderSunArc()
    }
    const onMode = () => this.#renderInsolation()
    const onNorms = () => this.#renderViolations()
    const onUi = () => {
      // Satellite toggle vs opacity slider have very different costs:
      // toggle requires a full tile fetch, slider is a visual tweak. Route
      // opacity-only changes through the cheap setOpacity() path so the
      // slider does not flicker through ~20-40 image reloads per drag.
      const prevSat = this._prevSat
      const curSat = editorStore.ui?.satelliteBg
      const curEnabled = !!curSat?.enabled
      const curOpacity = Number.isFinite(curSat?.opacity) ? curSat.opacity : 0.9
      const curProvider = curSat?.provider
      // A provider switch needs a full tile re-fetch (different source URLs),
      // same as an enable toggle. Only the opacity-only case takes the cheap
      // in-place path.
      if (!prevSat || prevSat.enabled !== curEnabled || prevSat.provider !== curProvider) {
        this.#renderSatellite()
        this.#renderPlot()
      } else if (curEnabled && prevSat.opacity !== curOpacity) {
        this.satelliteLayer.setOpacity(clamp01(curOpacity))
      }
      this._prevSat = { enabled: curEnabled, opacity: curOpacity, provider: curProvider }
      this.#renderEdgeLabels()
    }
    editorStore.addEventListener("plot:change", onPlot)
    editorStore.addEventListener("objects:change", onObjects)
    editorStore.addEventListener("selection:change", onSelection)
    editorStore.addEventListener("sun:change", onSun)
    editorStore.addEventListener("insolation:mode-change", onMode)
    editorStore.addEventListener("norms:change", onNorms)
    editorStore.addEventListener("ui:change", onUi)
    this._unsubscribe = () => {
      editorStore.removeEventListener("plot:change", onPlot)
      editorStore.removeEventListener("objects:change", onObjects)
      editorStore.removeEventListener("selection:change", onSelection)
      editorStore.removeEventListener("sun:change", onSun)
      editorStore.removeEventListener("insolation:mode-change", onMode)
      editorStore.removeEventListener("norms:change", onNorms)
      editorStore.removeEventListener("ui:change", onUi)
    }
  }

  #renderPlot() {
    this.plotLayer.destroyChildren()
    const points = editorStore.plot.polygonM.flatMap(([xM, yM]) => {
      const p = metresToPx({ xM, yM }, this.scale)
      return [p.x, p.y]
    })
    // When the satellite backdrop is on, the plot boundary is the only cue
    // telling the user "this is MY plot, not the neighbour's". Plain grey
    // dashes vanish into grass/pavement; a white halo underneath + a dark
    // dashed line on top gives high contrast on any background.
    const satOn = !!editorStore.ui?.satelliteBg?.enabled && !!editorStore.plot?.originLngLat
    if (satOn) {
      this.plotLayer.add(new Konva.Line({
        points,
        closed: true,
        stroke: "#ffffff",
        strokeWidth: 5,
        opacity: 0.85,
        listening: false,
      }))
    }
    this.plotLayer.add(new Konva.Line({
      points,
      closed: true,
      fill: satOn ? undefined : "#f7f3e9",
      stroke: satOn ? "#1e1b4b" : "#6b7280",
      strokeWidth: 2,
      dash: [6, 4],
    }))
    this.plotLayer.batchDraw()
  }

  #renderSatellite() {
    const ui = editorStore.ui?.satelliteBg
    if (!ui?.enabled) {
      this.satelliteLayer.clear()
      return
    }
    this.satelliteLayer.render({
      plot: editorStore.plot,
      scale: this.scale,
      stageWidth: this.stage?.width() ?? 0,
      stageHeight: this.stage?.height() ?? 0,
      stageScale: this.stage?.scaleX() ?? 1,
      stagePos: this.stage?.position() ?? { x: 0, y: 0 },
      opacity: clamp01(ui.opacity ?? 0.9),
      provider: ui.provider,
    })
  }

  // Stage 6d: one label per plot edge with the edge length in metres.
  // Skipped when the user has turned dimensions off in the menu sheet.
  // Each label is a small group (bg rect + text) centred on the edge
  // midpoint; it emits `plot:edge-click` on tap so the edge-edit popover
  // can open (wired in Stage 6d.2).
  #renderEdgeLabels() {
    if (!this.edgeLayer) return
    // Destroy only the labels, not the ghost preview line — otherwise a
    // plot:change / ui:change / pending-add:change during active typing in
    // the edge popover would wipe the preview for a full debounce cycle
    // (~120ms) before it regenerates. Ghost survives with its name="edge-preview".
    this.edgeLayer.find(".edge-label").forEach((n) => n.destroy())
    // Hide while a placement is armed — otherwise label hit-rects steal
    // taps from tap-to-place along the plot boundary.
    const hiddenByAdd = !!editorStore.pendingAddType
    if (!this.scale || !editorStore.plot || !editorStore.ui?.showEdgeDimensions || hiddenByAdd) {
      this.edgeLayer.batchDraw()
      return
    }
    const edges = polygonEdges(editorStore.plot.polygonM)
    edges.forEach((edge, idx) => {
      const lenM = edgeLengthM(edge)
      // Skip zero-length edges (shouldn't happen, but defensively).
      if (lenM <= 0) return
      const mid = edgeMidpointM(edge)
      const midPx = metresToPx({ xM: mid[0], yM: mid[1] }, this.scale)
      // Align the label with near-horizontal edges; for steep edges keep the
      // text horizontal so it's always readable without tilting the phone.
      const theta = edgeAngleRad(edge)
      let rotationDeg = 0
      const horizontal = Math.abs(Math.sin(theta)) < Math.SQRT1_2 // |angle| within ±45° of horizontal
      if (horizontal) {
        let deg = (theta * 180) / Math.PI
        if (deg > 90) deg -= 180
        else if (deg < -90) deg += 180
        rotationDeg = deg
      }
      const text = new Konva.Text({
        text: formatEdgeLength(lenM),
        fontSize: 12,
        fontFamily: "Nunito, sans-serif",
        fontStyle: "600",
        fill: "#1f2937",
        padding: 6,
      })
      const w = text.width()
      const h = text.height()
      // Centre the text on the group origin so rotation pivots neatly.
      text.offsetX(w / 2)
      text.offsetY(h / 2)
      const bg = new Konva.Rect({
        width: w,
        height: h,
        offsetX: w / 2,
        offsetY: h / 2,
        fill: "rgba(255,255,255,0.94)",
        stroke: "#cbd5e1",
        strokeWidth: 1,
        cornerRadius: 6,
      })
      const group = new Konva.Group({
        x: midPx.x,
        y: midPx.y,
        rotation: rotationDeg,
        name: "edge-label",
      })
      group.setAttr("edgeIdx", idx)
      group.add(bg, text)
      group.on("click tap", (e) => {
        // Prevent the stage's tap-to-place / click-to-select handler from
        // firing when the user meant to edit the edge.
        e.cancelBubble = true
        // Convert native group coords to viewport coords through the stage's
        // absolute transform — otherwise the popover opens in the wrong
        // place after pinch-zoom (stage.scale() and stage.position() apply
        // on top of the fit-scale math).
        const abs = group.getAbsolutePosition()
        const stageBounds = this.stageTarget.getBoundingClientRect()
        window.dispatchEvent(new CustomEvent("plot:edge-click", {
          detail: {
            edgeIdx: idx,
            lenM,
            viewportX: stageBounds.left + abs.x,
            viewportY: stageBounds.top + abs.y,
          },
        }))
      })
      this.edgeLayer.add(group)
    })
    this.edgeLayer.batchDraw()
  }

  #renderObjects() {
    this.objectLayer.destroyChildren()
    if (!this.scale) { this.objectLayer.batchDraw(); return }
    for (const obj of editorStore.objects) {
      const pos = metresToPx({ xM: obj.xM, yM: obj.yM }, this.scale)
      const rect = new Konva.Rect({
        id: obj.id,
        x: pos.x,
        y: pos.y,
        width: obj.widthM * this.scale.pxPerMetre,
        height: obj.heightM * this.scale.pxPerMetre,
        rotation: obj.rotationDeg ?? 0,
        fill: obj.fillColor ?? "#c7d2fe",
        stroke: obj.strokeColor ?? "#4338ca",
        strokeWidth: 2,
        draggable: !this._readOnly,
      })
      if (!this._readOnly) {
        rect.on("dragend", () => this.#commitTransform(rect, obj.id))
        rect.on("transformend", () => this.#commitTransform(rect, obj.id, { applyScale: true }))
      }
      this.objectLayer.add(rect)
      this.#renderHousePlanIndicator(obj)
    }
    this.objectLayer.batchDraw()
  }

  // Stage 9a: faint preview of a house's interior wall-graph, drawn inside its
  // footprint rect so the user sees "this house has a plan". The plan is fit
  // uniformly (shape-preserving) into the rect and rotated with it. Lines are
  // non-listening so they never steal drag/select from the rect beneath.
  #renderHousePlanIndicator(obj) {
    if (obj.type !== "house" || !this.scale) return
    const graph = obj.houseDesign?.floors?.[0]?.graph
    if (!graph?.walls?.length) return
    const bb = graphBoundingBox(graph)
    if (bb.widthM <= 0 || bb.heightM <= 0) return

    // Draw the plan at TRUE 1:1 metre size, anchored at the house rect's
    // top-left — never stretch it to fit the rect (that would make the plan's
    // dimensions disagree with the site). house_mode keeps the rect size equal
    // to the plan bbox, so 1:1 fills the rect exactly; if they ever differ, the
    // plan keeps its real size instead of lying.
    const rad = ((obj.rotationDeg ?? 0) * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const toPlot = (vx, vy) => {
      const lx = vx - bb.minX
      const ly = vy - bb.minY
      return { xM: obj.xM + lx * cos - ly * sin, yM: obj.yM + lx * sin + ly * cos }
    }
    // Faint room tints (Stage 9b) under the wall lines, so the site view hints
    // at the interior layout without competing with the walls.
    for (const room of obj.houseDesign?.floors?.[0]?.rooms ?? []) {
      if (!Array.isArray(room.polygon) || room.polygon.length < 3) continue
      const pts = room.polygon.flatMap(([vx, vy]) => {
        const p = metresToPx(toPlot(vx, vy), this.scale)
        return [p.x, p.y]
      })
      this.objectLayer.add(new Konva.Line({
        points: pts,
        closed: true,
        fill: roomColor(room),
        opacity: 0.3,
        listening: false,
      }))
    }
    const byId = new Map(graph.vertices.map((v) => [v.id, v]))
    for (const w of graph.walls) {
      const a = byId.get(w.a)
      const b = byId.get(w.b)
      if (!a || !b) continue
      const pa = metresToPx(toPlot(a.xM, a.yM), this.scale)
      const pb = metresToPx(toPlot(b.xM, b.yM), this.scale)
      this.objectLayer.add(new Konva.Line({
        points: [pa.x, pa.y, pb.x, pb.y],
        stroke: obj.strokeColor ?? "#4338ca",
        strokeWidth: 1,
        opacity: 0.55,
        listening: false,
      }))
    }
  }

  #commitTransform(rect, id, { applyScale = false } = {}) {
    let widthM, heightM
    if (applyScale) {
      // Konva resizes via scaleX/scaleY; bake back into width/height.
      widthM = (rect.width() * rect.scaleX()) / this.scale.pxPerMetre
      heightM = (rect.height() * rect.scaleY()) / this.scale.pxPerMetre
      rect.width(widthM * this.scale.pxPerMetre)
      rect.height(heightM * this.scale.pxPerMetre)
      rect.scaleX(1)
      rect.scaleY(1)
    }
    const metres = pxToMetres({ x: rect.x(), y: rect.y() }, this.scale)
    const patch = {
      xM: snap(metres.xM),
      yM: snap(metres.yM),
      rotationDeg: Math.round(rect.rotation() * 10) / 10,
    }
    if (applyScale) {
      patch.widthM = Math.max(0.3, snap(widthM))
      patch.heightM = Math.max(0.3, snap(heightM))
    }
    editorStore.updateObject(id, patch)
  }

  #renderInsolation() {
    // Clear both layers each time; mode selects which to populate.
    this.shadowLayer.destroyChildren()
    this.heatmapLayer.destroyChildren()

    const mode = editorStore.insolationMode
    if (mode === "shadows") this.#renderShadows()
    else if (mode === "heatmap") this.#renderHeatmap()

    this.shadowLayer.batchDraw()
    this.heatmapLayer.batchDraw()
  }

  #renderShadows() {
    if (!this.scale) return
    const sun = this.#currentSun()
    if (!sun || sun.altitude <= 0) return

    for (const obj of editorStore.objects) {
      const poly = computeShadowPolygon(obj, sun.azimuth, sun.altitude)
      if (!poly || poly.length < 3) continue
      const points = poly.flatMap(([xM, yM]) => {
        const p = metresToPx({ xM, yM }, this.scale)
        return [p.x, p.y]
      })
      this.shadowLayer.add(new Konva.Line({
        points, closed: true, fill: "rgba(30,30,45,0.35)", stroke: "rgba(30,30,45,0.6)", strokeWidth: 1,
      }))
    }
  }

  #renderHeatmap() {
    if (!this.scale || !editorStore.plot) return
    const latLng = this.#effectiveLatLng()
    if (!latLng) return

    // _heatmapCache: shape returned by computeHeatmap() —
    //   { sunMinutes: Float32Array, cols, rows, resolutionM, bb, daylightMinutes, inPlot }
    // _heatmapCacheKey tracks inputs that invalidate the cache: the day
    // part of dateIso, the UTC offset, and the current objects digest.
    const result = this._heatmapCache ?? computeHeatmap({
      plot: editorStore.plot,
      objects: editorStore.objects,
      lat: latLng[0],
      lng: latLng[1],
      dateIso: editorStore.sun.dateIso,
      utcOffsetHours: this.#effectiveUtcOffset(),
      resolutionM: 0.5,
      stepMinutes: 15,
    })
    this._heatmapCache = result
    this._heatmapCacheKey = this.#currentHeatmapKey()

    const { data, width, height } = heatmapToImageData(result)
    const offscreen = document.createElement("canvas")
    offscreen.width = width
    offscreen.height = height
    offscreen.getContext("2d").putImageData(new ImageData(data, width, height), 0, 0)

    const origin = metresToPx({ xM: result.bb.minX, yM: result.bb.minY }, this.scale)
    const widthPx = result.bb.widthM * this.scale.pxPerMetre
    const heightPx = result.bb.heightM * this.scale.pxPerMetre

    this.heatmapLayer.add(new Konva.Image({
      image: offscreen,
      x: origin.x,
      y: origin.y,
      width: widthPx,
      height: heightPx,
      opacity: 0.75,
      // Pixel-sharp scaling keeps 0.5 m cells crisp instead of blurring
      // neighbouring values together (which would misrepresent the data).
      imageSmoothingEnabled: false,
      listening: false,
    }))
  }

  #currentHeatmapKey() {
    const dayPart = (editorStore.sun.dateIso || "").slice(0, 10)
    return `${dayPart}|${this.#effectiveUtcOffset()}`
  }

  #heatmapKeyChanged() {
    return this._heatmapCacheKey !== this.#currentHeatmapKey()
  }

  #invalidateHeatmap() {
    this._heatmapCache = null
    this._heatmapCacheKey = null
  }

  #renderViolations() {
    if (!this.violationRenderer) return
    const ruleSet = loadRuleSet(editorStore.norms.ruleSetLocale, editorStore.norms.ruleSetVersion)
    const violations = validateNorms({
      plot: editorStore.plot,
      objects: editorStore.objects,
      ruleSet,
      disabledRuleIds: editorStore.norms.disabledRuleIds,
    })
    const byIdMap = violationsByObjectId(violations)
    this.violationRenderer.render({
      violations, byIdMap, objects: editorStore.objects, scale: this.scale,
    })
  }

  #renderSunArc() {
    this.sunArcLayer.destroyChildren()
    if (!this.scale || !editorStore.plot) { this.sunArcLayer.batchDraw(); return }
    const latLng = this.#effectiveLatLng()
    if (!latLng) { this.sunArcLayer.batchDraw(); return }

    const bb = polygonBoundingBox(editorStore.plot.polygonM)
    const centre = { xM: (bb.minX + bb.maxX) / 2, yM: (bb.minY + bb.maxY) / 2 }
    const radiusM = Math.min(bb.widthM, bb.heightM) * 0.5

    const samples = sunArcSamples(editorStore.sun.dateIso, latLng[0], latLng[1], this.#effectiveUtcOffset(), 30)
    if (samples.length < 2) { this.sunArcLayer.batchDraw(); return }

    const points = []
    for (const s of samples) {
      const { dx, dy } = shadowDirection(s.azimuth)
      // Point lies on horizon circle in the direction *from centre toward sun*
      // = opposite of shadow direction. Radius scaled by cos(altitude) so the
      // arc shows the sun's horizontal angular position.
      const r = radiusM * Math.cos(s.altitude)
      const xM = centre.xM - dx * r
      const yM = centre.yM - dy * r
      const p = metresToPx({ xM, yM }, this.scale)
      points.push(p.x, p.y)
    }
    this.sunArcLayer.add(new Konva.Line({
      points, stroke: "#f59e0b", strokeWidth: 1.5, dash: [4, 3], listening: false,
    }))

    // Current-sun marker.
    const now = this.#currentSun()
    if (now && now.altitude > 0) {
      const { dx, dy } = shadowDirection(now.azimuth)
      const r = radiusM * Math.cos(now.altitude)
      const p = metresToPx({ xM: centre.xM - dx * r, yM: centre.yM - dy * r }, this.scale)
      this.sunArcLayer.add(new Konva.Circle({ x: p.x, y: p.y, radius: 6, fill: "#f59e0b", stroke: "#92400e", strokeWidth: 2 }))
    }
    this.sunArcLayer.batchDraw()
  }

  #sunDate() {
    return toUtcDate(editorStore.sun.dateIso, this.#effectiveUtcOffset())
  }

  #currentSun() {
    const latLng = this.#effectiveLatLng()
    if (!latLng) return null
    return getSunPosition(this.#sunDate(), latLng[0], latLng[1])
  }

  #effectiveLatLng() {
    const origin = editorStore.plot?.originLngLat
    if (origin && Array.isArray(origin)) return [origin[1], origin[0]]
    return null
  }

  #effectiveUtcOffset() {
    const override = editorStore.sun.utcOffsetHoursOverride
    if (override != null) return override
    const origin = editorStore.plot?.originLngLat
    return origin ? estimateUtcOffsetHours(origin[0]) : 0
  }

  #reattachTransformer() {
    // No transformer in read-only mode — method becomes a no-op.
    if (!this.transformer) return
    const id = editorStore.selectedId
    if (!id) {
      this.transformer.nodes([])
      this.uiLayer.batchDraw()
      return
    }
    const node = this.objectLayer.findOne(`#${cssId(id)}`)
    this.transformer.nodes(node ? [node] : [])
    // A house with a floor plan owns its size through the plan — disable resize
    // anchors on the site (rotate + move stay) so the rect can't desync from the
    // plan. Other objects resize normally.
    const obj = editorStore.objects.find((o) => o.id === id)
    const sizeLocked = obj?.type === "house" && !!obj.houseDesign
    this.transformer.resizeEnabled(!sizeLocked)
    this.uiLayer.batchDraw()
  }

  get debugHandle() {
    return {
      store: editorStore,
      scale: this.scale,
      stage: this.stage,
      edgeLayer: this.edgeLayer,
    }
  }
}

function snap(x) {
  return Math.round(x / SNAP_METRES) * SNAP_METRES
}

function clamp01(n) {
  return Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0))
}

// Konva `findOne("#id")` parses the argument as a CSS selector. Ids are
// generated by store.randomId() with an "obj-" prefix (never start with
// a digit), but we still escape any non-word/dash characters defensively.
function cssId(id) {
  return id.replace(/[^\w-]/g, (c) => `\\${c}`)
}

// Adaptive precision for edge labels: short edges need centimetre detail,
// long edges look cleaner as whole metres. Trailing zeros are trimmed so a
// neat 50 m edge reads "50 м", not "50.0 м". Always rendered in metres.
function formatEdgeLength(lenM) {
  let s
  if (lenM < 10) s = lenM.toFixed(2)
  else if (lenM < 100) s = lenM.toFixed(1)
  else s = String(Math.round(lenM))
  if (s.includes(".")) s = s.replace(/0+$/, "").replace(/\.$/, "")
  return `${s} м`
}

